<template>
    <div class="ops-trip-page" v-loading="loading">
        <div class="page-title d-flex justify-content-between align-items-center flex-wrap">
            <div class="title">
                <h3>Trip fulfilment</h3>
            </div>
            <div class="d-flex align-items-center gap-2">
                <el-tag v-if="summary" :type="healthTagType(summary.health)" effect="dark" size="large">
                    {{ healthLabel(summary.health) }}
                </el-tag>
                <el-button size="small" @click="load">Refresh</el-button>
                <el-button size="small" type="primary" @click="publishDialog = true">Publish update</el-button>
            </div>
        </div>
        <p class="trip-meta mb-3" v-if="enquiry">
            CDM{{ enquiry.id }} · {{ enquiry.name }} · {{ enquiry.destinations }}
            <span v-if="enquiry.departure_date"> · Dep {{ formatDate(enquiry.departure_date) }}</span>
        </p>

        <el-alert v-if="error && !needsTemplate" :title="error" type="warning" show-icon class="mb-3" />

        <el-card v-if="needsTemplate" class="mb-3" shadow="never" v-loading="templatesLoading">
            <h5 class="mb-2">Select checklist template</h5>
            <p class="text-muted mb-3">
                Operation handover is accepted. Choose a template to create this trip’s checklist.
            </p>
            <el-form label-position="top" @submit.prevent>
                <el-form-item label="Checklist template" required>
                    <el-select v-model="selectedTemplateId" placeholder="Select template" style="width: 100%; max-width: 420px" filterable>
                        <el-option
                            v-for="tpl in templateOptions"
                            :key="tpl.id"
                            :label="tpl.name + (tpl.is_default ? ' (Default)' : '') + ' · ' + tpl.items_count + ' items'"
                            :value="tpl.id"
                        />
                    </el-select>
                </el-form-item>
                <el-button type="primary" :loading="instantiating" :disabled="!selectedTemplateId" @click="createChecklist">
                    Create checklist
                </el-button>
            </el-form>
        </el-card>

        <template v-if="summary">
            <el-row :gutter="16" class="mb-3">
                <el-col :xs="24" :sm="8">
                    <el-card shadow="hover" class="metric-card">
                        <div class="metric-label">Tasks complete</div>
                        <div class="metric-value success">{{ summary.tasks_complete }}/{{ summary.tasks_total }}</div>
                    </el-card>
                </el-col>
                <el-col :xs="24" :sm="8">
                    <el-card shadow="hover" class="metric-card">
                        <div class="metric-label">Overdue</div>
                        <div class="metric-value danger">{{ summary.overdue }}</div>
                    </el-card>
                </el-col>
                <el-col :xs="24" :sm="8">
                    <el-card shadow="hover" class="metric-card">
                        <div class="metric-label">Blocked</div>
                        <div class="metric-value">{{ summary.blocked }}</div>
                    </el-card>
                </el-col>
            </el-row>

            <el-card class="mb-3" shadow="never">
                <div class="d-flex justify-content-between mb-2">
                    <strong>Overall completion</strong>
                    <span>{{ summary.completion_percent }}%</span>
                </div>
                <el-progress :percentage="Number(summary.completion_percent)" :stroke-width="14" status="success" />
                <el-tag v-if="summary.is_ready" type="success" class="mt-2">Ready for customer delivery</el-tag>
                <el-tag v-else type="warning" class="mt-2">Critical pre-trip items incomplete</el-tag>
            </el-card>

            <el-card class="mb-3" shadow="never">
                <div class="stage-stepper">
                    <div
                        v-for="stage in summary.stages"
                        :key="stage.key"
                        class="stage-step"
                        :class="{ current: stage.current, done: stage.complete }"
                    >
                        <div class="stage-dot">
                            <i v-if="stage.complete" class="fa-solid fa-check"></i>
                            <span v-else>{{ stage.done }}/{{ stage.total }}</span>
                        </div>
                        <div class="stage-label">{{ stage.label }}</div>
                    </div>
                </div>
            </el-card>

            <el-card class="mb-3" shadow="never">
                <template #header><strong>Target curve</strong></template>
                <el-table :data="summary.target_curve" size="small" border>
                    <el-table-column prop="label" label="Checkpoint" />
                    <el-table-column prop="expected_percent" label="Expected" width="100">
                        <template #default="scope">{{ scope.row.expected_percent }}%</template>
                    </el-table-column>
                    <el-table-column prop="actual_percent" label="Actual" width="100">
                        <template #default="scope">{{ scope.row.actual_percent != null ? scope.row.actual_percent + '%' : '—' }}</template>
                    </el-table-column>
                    <el-table-column prop="gap" label="Gap" width="100">
                        <template #default="scope">
                            <span v-if="scope.row.gap != null" :class="scope.row.gap < 0 ? 'text-danger' : 'text-success'">
                                {{ scope.row.gap > 0 ? '+' : '' }}{{ scope.row.gap }} pp
                            </span>
                            <span v-else>—</span>
                        </template>
                    </el-table-column>
                    <el-table-column prop="status" label="Status" width="120">
                        <template #default="scope">
                            <el-tag :type="curveTag(scope.row.status)" size="small">{{ scope.row.status }}</el-tag>
                        </template>
                    </el-table-column>
                </el-table>
            </el-card>

            <el-card shadow="never">
                <template #header>
                    <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div>
                            <strong>Checklist</strong>
                            <el-tag v-if="checklistMeta.template_name" size="small" class="ml-2" type="info">
                                {{ checklistMeta.template_name }}
                            </el-tag>
                        </div>
                        <div class="d-flex align-items-center gap-2 flex-wrap" v-if="selectedIds.length">
                            <span class="text-muted small">{{ selectedIds.length }} selected</span>
                            <el-button size="small" type="primary" @click="openBulkUpdate">Update selected</el-button>
                            <el-button size="small" @click="clearSelection">Clear</el-button>
                        </div>
                    </div>
                </template>

                <el-tabs v-model="activeStageTab" class="checklist-tabs">
                    <el-tab-pane
                        v-for="tab in stageTabs"
                        :key="tab.key"
                        :name="tab.key"
                    >
                        <template #label>
                            <span>{{ tab.label }}</span>
                            <el-badge
                                :value="tab.done + '/' + tab.total"
                                :type="tab.complete ? 'success' : (tab.current ? 'warning' : 'info')"
                                class="ml-2 stage-tab-badge"
                            />
                        </template>

                        <div v-if="tab.items.length === 0" class="text-muted py-3">No tasks in this stage.</div>
                        <div v-else class="bulk-select-bar mb-2">
                            <el-checkbox
                                :model-value="isTabFullySelected(tab)"
                                :indeterminate="isTabPartiallySelected(tab)"
                                @change="(val) => toggleSelectTab(tab, val)"
                            >
                                Select all in {{ tab.label }}
                            </el-checkbox>
                        </div>
                        <div
                            v-for="item in tab.items"
                            :key="item.id"
                            class="checklist-row"
                            :class="{ selected: selectedIds.includes(item.id) }"
                            @click="openItem(item)"
                        >
                            <div class="row-check" @click.stop>
                                <el-checkbox
                                    :model-value="selectedIds.includes(item.id)"
                                    @change="(val) => toggleSelectItem(item.id, val)"
                                />
                            </div>
                            <div class="row-icon" :class="item.display_status">
                                <i v-if="item.display_status === 'complete'" class="fa-solid fa-circle-check"></i>
                                <i v-else-if="item.display_status === 'overdue'" class="fa-solid fa-circle-exclamation"></i>
                                <i v-else-if="item.display_status === 'blocked'" class="fa-solid fa-ban"></i>
                                <i v-else-if="item.display_status === 'in_progress'" class="fa-solid fa-clock"></i>
                                <i v-else class="fa-regular fa-circle"></i>
                            </div>
                            <div class="row-body">
                                <div class="row-title">
                                    {{ item.title }}
                                    <el-tag v-if="item.is_critical" size="small" type="danger" effect="plain" class="ml-1">Critical</el-tag>
                                    <el-tag v-if="isAutoSynced(item)" size="small" type="success" effect="plain" class="ml-1">Auto</el-tag>
                                </div>
                                <div class="row-meta">
                                    {{ item.owner_name || item.owner_role }}
                                    <span v-if="item.due_at"> · Due {{ item.due_at }}</span>
                                </div>
                            </div>
                            <el-tag :type="statusTag(item.display_status)" size="small">{{ statusLabel(item.display_status) }}</el-tag>
                        </div>
                    </el-tab-pane>
                </el-tabs>
            </el-card>
        </template>

        <el-drawer v-model="drawer" title="Update task" size="420px">
            <template v-if="activeItem">
                <h5>{{ activeItem.title }}</h5>
                <p class="text-muted">{{ activeItem.stage_label }} · {{ activeItem.owner_role }}</p>

                <el-form label-position="top">
                    <el-form-item label="Status">
                        <el-select v-model="editForm.status" style="width: 100%">
                            <el-option label="Pending" value="pending" />
                            <el-option label="In progress" value="in_progress" />
                            <el-option label="Complete" value="complete" />
                            <el-option label="Blocked" value="blocked" />
                            <el-option label="Skipped" value="skipped" />
                        </el-select>
                    </el-form-item>
                    <el-form-item label="Notes">
                        <el-input v-model="editForm.notes" type="textarea" :rows="3" />
                    </el-form-item>

                    <template v-if="isConfirmationCall(activeItem)">
                        <el-divider>Confirmation call</el-divider>
                        <el-form-item label="Contact name">
                            <el-input v-model="editForm.meta.contact_name" />
                        </el-form-item>
                        <el-form-item label="Contact phone">
                            <el-input v-model="editForm.meta.contact_phone" />
                        </el-form-item>
                        <el-form-item label="Channel">
                            <el-select v-model="editForm.meta.channel" style="width: 100%">
                                <el-option label="Phone" value="phone" />
                                <el-option label="WhatsApp" value="whatsapp" />
                                <el-option label="Email" value="email" />
                            </el-select>
                        </el-form-item>
                        <el-form-item label="Outcome">
                            <el-select v-model="editForm.meta.outcome" style="width: 100%">
                                <el-option label="Confirmed" value="confirmed" />
                                <el-option label="Changed" value="changed" />
                                <el-option label="No answer" value="no_answer" />
                                <el-option label="Escalated" value="escalated" />
                            </el-select>
                        </el-form-item>
                        <el-form-item label="Change notes">
                            <el-input v-model="editForm.meta.change_notes" type="textarea" :rows="2" />
                        </el-form-item>
                    </template>

                    <template v-if="isDriverDetails(activeItem)">
                        <el-divider>Driver details</el-divider>
                        <el-form-item label="Driver name"><el-input v-model="editForm.meta.driver_name" /></el-form-item>
                        <el-form-item label="Driver mobile"><el-input v-model="editForm.meta.driver_mobile" /></el-form-item>
                        <el-form-item label="Vehicle number"><el-input v-model="editForm.meta.vehicle_number" /></el-form-item>
                        <el-form-item label="Vehicle type"><el-input v-model="editForm.meta.vehicle_type" /></el-form-item>
                        <el-form-item label="Supplier"><el-input v-model="editForm.meta.supplier_name" /></el-form-item>
                    </template>

                    <template v-if="isCustomerIssue(activeItem) || isIncident(activeItem)">
                        <el-divider>Issue / incident</el-divider>
                        <el-form-item label="Summary"><el-input v-model="editForm.meta.issue_summary" type="textarea" :rows="2" /></el-form-item>
                        <el-form-item label="Severity">
                            <el-select v-model="editForm.meta.severity" style="width: 100%">
                                <el-option label="Low" value="low" />
                                <el-option label="Medium" value="medium" />
                                <el-option label="High" value="high" />
                            </el-select>
                        </el-form-item>
                        <el-form-item label="Ticket ID"><el-input v-model="editForm.meta.ticket_id" /></el-form-item>
                        <el-form-item label="Resolution notes"><el-input v-model="editForm.meta.resolution_notes" type="textarea" :rows="2" /></el-form-item>
                    </template>

                    <el-button type="primary" :loading="saving" @click="saveItem" style="width: 100%">Save</el-button>
                </el-form>
            </template>
        </el-drawer>

        <el-dialog v-model="publishDialog" title="Publish customer update" width="480px">
            <el-input v-model="publishMessage" type="textarea" :rows="5" placeholder="Message to customer..." />
            <template #footer>
                <el-button @click="publishDialog = false">Cancel</el-button>
                <el-button type="primary" :loading="publishing" @click="publishUpdate">Send</el-button>
            </template>
        </el-dialog>

        <el-dialog v-model="bulkDialog" title="Update selected tasks" width="480px">
            <p class="text-muted mb-3">Apply the same update to {{ selectedIds.length }} selected task(s).</p>
            <el-form label-position="top">
                <el-form-item label="Status">
                    <el-select v-model="bulkForm.status" clearable placeholder="Keep current status" style="width: 100%">
                        <el-option label="Pending" value="pending" />
                        <el-option label="In progress" value="in_progress" />
                        <el-option label="Complete" value="complete" />
                        <el-option label="Blocked" value="blocked" />
                        <el-option label="Skipped" value="skipped" />
                    </el-select>
                </el-form-item>
                <el-form-item label="Notes">
                    <el-input v-model="bulkForm.notes" type="textarea" :rows="3" placeholder="Optional notes for all selected" />
                </el-form-item>
            </el-form>
            <template #footer>
                <el-button @click="bulkDialog = false">Cancel</el-button>
                <el-button type="primary" :loading="bulkSaving" @click="saveBulkUpdate">Update all</el-button>
            </template>
        </el-dialog>
    </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from 'vue';
import axios from 'axios';
import { ElMessage } from 'element-plus';

const props = defineProps({
    enquiryId: { type: [Number, String], required: true },
});

const loading = ref(false);
const saving = ref(false);
const publishing = ref(false);
const instantiating = ref(false);
const templatesLoading = ref(false);
const error = ref('');
const needsTemplate = ref(false);
const enquiry = ref(null);
const summary = ref(null);
const items = ref([]);
const checklistMeta = reactive({
    template_id: null,
    template_name: '',
});
const templateOptions = ref([]);
const selectedTemplateId = ref(null);
const activeStageTab = ref('booking');
const PRIMARY_STAGE_TABS = ['booking', 'fulfilment', 'qa'];
const selectedIds = ref([]);
const drawer = ref(false);
const activeItem = ref(null);
const publishDialog = ref(false);
const publishMessage = ref('');
const bulkDialog = ref(false);
const bulkSaving = ref(false);
const bulkForm = reactive({
    status: '',
    notes: '',
});
const editForm = reactive({
    status: 'pending',
    notes: '',
    meta: {},
});

const stageTabs = computed(() => {
    const byStage = {};
    items.value.forEach((item) => {
        if (!byStage[item.stage]) {
            byStage[item.stage] = [];
        }
        byStage[item.stage].push(item);
    });

    const summaryStages = (summary.value && summary.value.stages) ? summary.value.stages : [];
    const summaryMap = {};
    summaryStages.forEach((s) => {
        summaryMap[s.key] = s;
    });

    const keys = [...PRIMARY_STAGE_TABS];
    summaryStages.forEach((s) => {
        if (!keys.includes(s.key) && (byStage[s.key] || []).length > 0) {
            keys.push(s.key);
        }
    });
    Object.keys(byStage).forEach((key) => {
        if (!keys.includes(key)) {
            keys.push(key);
        }
    });

    return keys.map((key) => {
        const stageItems = byStage[key] || [];
        const meta = summaryMap[key] || {};
        const label = meta.label
            || (stageItems[0] && stageItems[0].stage_label)
            || key;
        const done = meta.done != null
            ? meta.done
            : stageItems.filter((i) => i.status === 'complete' || i.status === 'skipped').length;
        const total = meta.total != null ? meta.total : stageItems.length;

        return {
            key,
            label,
            items: stageItems,
            done,
            total,
            current: !!meta.current,
            complete: !!meta.complete || (total > 0 && done >= total),
        };
    });
});

const clearSelection = () => {
    selectedIds.value = [];
};

const toggleSelectItem = (id, checked) => {
    const exists = selectedIds.value.includes(id);
    if (checked && !exists) {
        selectedIds.value = [...selectedIds.value, id];
    } else if (!checked && exists) {
        selectedIds.value = selectedIds.value.filter((x) => x !== id);
    }
};

const isTabFullySelected = (tab) => {
    if (!tab.items.length) return false;
    return tab.items.every((item) => selectedIds.value.includes(item.id));
};

const isTabPartiallySelected = (tab) => {
    if (!tab.items.length) return false;
    const count = tab.items.filter((item) => selectedIds.value.includes(item.id)).length;
    return count > 0 && count < tab.items.length;
};

const toggleSelectTab = (tab, checked) => {
    const ids = tab.items.map((item) => item.id);
    if (checked) {
        selectedIds.value = Array.from(new Set([...selectedIds.value, ...ids]));
    } else {
        selectedIds.value = selectedIds.value.filter((id) => !ids.includes(id));
    }
};

const openBulkUpdate = () => {
    if (!selectedIds.value.length) {
        ElMessage.warning('Select at least one task');
        return;
    }
    bulkForm.status = '';
    bulkForm.notes = '';
    bulkDialog.value = true;
};

const saveBulkUpdate = async () => {
    if (!selectedIds.value.length) {
        ElMessage.warning('Select at least one task');
        return;
    }
    if (!bulkForm.status && !String(bulkForm.notes || '').trim()) {
        ElMessage.warning('Choose a status and/or enter notes');
        return;
    }

    bulkSaving.value = true;
    try {
        const payload = {
            item_ids: selectedIds.value,
        };
        if (bulkForm.status) {
            payload.status = bulkForm.status;
        }
        if (String(bulkForm.notes || '').trim() !== '') {
            payload.notes = bulkForm.notes;
        }

        const { data } = await axios.post(
            '/operations/trips/' + props.enquiryId + '/items/bulk',
            payload
        );

        if (data.items) {
            items.value = data.items;
        }
        if (data.summary) {
            summary.value = data.summary;
        }

        if (data.updated > 0) {
            ElMessage.success(`Updated ${data.updated} task(s)`);
        }
        if (data.failed > 0) {
            const first = (data.errors && data.errors[0]) ? data.errors[0].error : 'Some tasks could not be updated';
            ElMessage.warning(`${data.failed} failed: ${first}`);
        }
        if (!data.success && !data.updated) {
            ElMessage.error((data.errors && data.errors[0] && data.errors[0].error) || data.error || 'Bulk update failed');
            return;
        }

        bulkDialog.value = false;
        clearSelection();
    } catch (e) {
        ElMessage.error(e.response?.data?.error || 'Bulk update failed');
    } finally {
        bulkSaving.value = false;
    }
};

const loadTemplateOptions = async () => {
    templatesLoading.value = true;
    try {
        const { data } = await axios.get('/operations/template-options');
        templateOptions.value = data.data || [];
        selectedTemplateId.value = data.default_id
            || (templateOptions.value[0] && templateOptions.value[0].id)
            || null;
    } catch (e) {
        templateOptions.value = [];
        selectedTemplateId.value = null;
        ElMessage.error('Could not load checklist templates');
    } finally {
        templatesLoading.value = false;
    }
};

const load = async () => {
    loading.value = true;
    error.value = '';
    needsTemplate.value = false;
    summary.value = null;
    items.value = [];
    selectedIds.value = [];
    try {
        const { data } = await axios.get('/operations/trips/' + props.enquiryId + '/details');
        enquiry.value = data.enquiry;
        summary.value = data.summary;
        items.value = data.items || [];
        checklistMeta.template_id = data.checklist?.template_id || null;
        checklistMeta.template_name = data.checklist?.template_name || '';
        const current = (data.summary?.stages || []).find((s) => s.current);
        if (current && current.key) {
            activeStageTab.value = current.key;
        } else if (!PRIMARY_STAGE_TABS.includes(activeStageTab.value)) {
            activeStageTab.value = 'booking';
        }
    } catch (e) {
        error.value = e.response?.data?.error || 'Failed to load trip fulfilment';
        if (e.response?.data?.enquiry) {
            enquiry.value = e.response.data.enquiry;
        }
        if (e.response?.data?.needs_template) {
            needsTemplate.value = true;
            await loadTemplateOptions();
        }
    } finally {
        loading.value = false;
    }
};

const createChecklist = async () => {
    if (!selectedTemplateId.value) {
        ElMessage.error('Select a checklist template');
        return;
    }
    instantiating.value = true;
    try {
        const { data } = await axios.post('/operations/trips/' + props.enquiryId + '/instantiate', {
            template_id: selectedTemplateId.value,
        });
        if (!data.success) {
            ElMessage.error(data.error || 'Could not create checklist');
            return;
        }
        ElMessage.success('Checklist created');
        needsTemplate.value = false;
        await load();
    } catch (e) {
        ElMessage.error(e.response?.data?.error || 'Could not create checklist');
    } finally {
        instantiating.value = false;
    }
};

const openItem = (item) => {
    activeItem.value = item;
    editForm.status = item.status;
    editForm.notes = item.notes || '';
    editForm.meta = Object.assign({}, item.meta || {});
    drawer.value = true;
};

const saveItem = async () => {
    if (!activeItem.value) return;
    saving.value = true;
    try {
        const { data } = await axios.post('/operations/items/' + activeItem.value.id, {
            status: editForm.status,
            notes: editForm.notes,
            meta: editForm.meta,
        });
        if (!data.success) {
            ElMessage.error(data.error || 'Save failed');
            return;
        }
        ElMessage.success('Task updated');
        drawer.value = false;
        await load();
    } catch (e) {
        ElMessage.error(e.response?.data?.error || 'Save failed');
    } finally {
        saving.value = false;
    }
};

const publishUpdate = async () => {
    publishing.value = true;
    try {
        const { data } = await axios.post('/operations/trips/' + props.enquiryId + '/publish-update', {
            message: publishMessage.value,
        });
        ElMessage[data.success ? 'success' : 'warning'](data.message);
        if (data.success) {
            publishDialog.value = false;
            publishMessage.value = '';
        }
    } catch (e) {
        ElMessage.error('Failed to publish update');
    } finally {
        publishing.value = false;
    }
};

const keyStartsWith = (item, prefix) => {
    const key = item && item.item_key ? String(item.item_key) : '';
    return key === prefix || key.startsWith(prefix + '_');
};

const isConfirmationCall = (item) => keyStartsWith(item, 'confirmation_call');
const isDriverDetails = (item) => keyStartsWith(item, 'driver_details');
const isCustomerIssue = (item) => keyStartsWith(item, 'customer_issue');
const isIncident = (item) => keyStartsWith(item, 'incident');
const isAutoSynced = (item) => {
    const meta = item && item.meta ? item.meta : {};
    return meta.source === 'enquiry_sync'
        || (item.notes && String(item.notes).includes('Auto-completed from enquiry data'));
};

const healthLabel = (h) => ({
    on_track: 'On track',
    warning: 'Warning',
    at_risk: 'At risk',
    critical: 'Critical',
}[h] || h);

const healthTagType = (h) => ({
    on_track: 'success',
    warning: 'warning',
    at_risk: 'warning',
    critical: 'danger',
}[h] || 'info');

const statusTag = (s) => ({
    complete: 'success',
    overdue: 'danger',
    blocked: 'danger',
    in_progress: 'warning',
    pending: 'info',
    skipped: 'info',
}[s] || 'info');

const statusLabel = (s) => ({
    complete: 'Complete',
    overdue: 'Overdue',
    blocked: 'Blocked',
    in_progress: 'In progress',
    pending: 'Pending',
    skipped: 'Skipped',
}[s] || s);

const curveTag = (s) => ({
    on_track: 'success',
    warning: 'warning',
    critical: 'danger',
    upcoming: 'info',
}[s] || 'info');

const formatDate = (value) => {
    if (!value) return '';
    const raw = String(value);
    // Already human-readable (e.g. d-m-Y)
    if (/^\d{2}-\d{2}-\d{4}$/.test(raw)) {
        return raw;
    }
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) {
        return raw.slice(0, 10);
    }
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
};

onMounted(load);
</script>

<style scoped>
.ops-trip-page { padding: 12px 4px 40px; }
.trip-meta {
    margin-top: 8px;
    color: #6c757d;
    font-size: 14px;
    line-height: 1.4;
}
.metric-card { text-align: center; }
.metric-label { font-size: 13px; color: #6c757d; }
.metric-value { font-size: 28px; font-weight: 700; margin-top: 4px; }
.metric-value.success { color: #28a745; }
.metric-value.danger { color: #dc3545; }
.stage-stepper { display: flex; flex-wrap: wrap; gap: 8px; justify-content: space-between; }
.stage-step { flex: 1; min-width: 100px; text-align: center; opacity: 0.55; }
.stage-step.current, .stage-step.done { opacity: 1; }
.stage-dot {
    width: 42px; height: 42px; border-radius: 50%; margin: 0 auto 6px;
    display: flex; align-items: center; justify-content: center;
    background: #e9ecef; font-size: 12px; font-weight: 600;
}
.stage-step.done .stage-dot { background: #28a745; color: #fff; }
.stage-step.current .stage-dot { background: #fd7e14; color: #fff; }
.stage-label { font-size: 12px; font-weight: 600; }
.checklist-tabs :deep(.el-tabs__header) { margin-bottom: 8px; }
.checklist-tabs :deep(.el-tabs__item) { font-weight: 600; }
.stage-tab-badge { vertical-align: middle; }
.stage-tab-badge :deep(.el-badge__content) { position: static; transform: none; }
.bulk-select-bar {
    padding: 4px 8px 8px;
    border-bottom: 1px solid #f0f0f0;
}
.checklist-row {
    display: flex; align-items: center; gap: 12px; padding: 12px 8px;
    border-bottom: 1px solid #eee; cursor: pointer;
}
.checklist-row:hover { background: #f8f9fa; }
.checklist-row.selected { background: #f0f7ff; }
.row-check { display: flex; align-items: center; }
.row-icon { width: 28px; text-align: center; font-size: 18px; color: #adb5bd; }
.row-icon.complete { color: #28a745; }
.row-icon.overdue, .row-icon.blocked { color: #dc3545; }
.row-icon.in_progress { color: #fd7e14; }
.row-body { flex: 1; }
.row-title { font-weight: 600; }
.row-meta { font-size: 12px; color: #6c757d; }
</style>
